import { useFormField } from "../../hooks";
import { FieldHint } from "../FieldHint";
import { Spinner } from "../Spinner";
import { inputClass, labelClass, resolveFieldState } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type StringField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends string ? K : never;
}[keyof IntakeFormData];

interface TextInputProps {
  field: StringField;
  label: string;
  validation: ReturnType<typeof useStepValidation>;
  type?: "text" | "email" | "tel" | "date";
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  autoComplete?: string;
  /** When true, the input shows the value but the user cannot edit it. Used for
   *  fields the AC marks "Read-Only Text (auto-populated)" like State. */
  readOnly?: boolean;
  /** When true, hide the current value and overlay a spinner — used while
   *  side-effects like the zip-code lookup are populating this field. */
  loading?: boolean;
  /** Static helper text rendered between the label and the input — used to
   *  set context BEFORE the user interacts (e.g. "Auto-populated from Zip Code"). */
  hint?: string;
  /** Override the default full-width layout — e.g. "max-w-xs" for short inputs like dates. */
  containerClassName?: string;
  /** When true, suppresses prefill and mismatch hints — use on steps where fields are never proposal-prefilled. */
  noPrefill?: boolean;
}

export function TextInput({
  field,
  label,
  validation,
  type = "text",
  placeholder,
  maxLength,
  required,
  autoComplete,
  readOnly,
  loading,
  hint,
  containerClassName,
  noPrefill,
}: TextInputProps) {
  const f = useFormField(field, validation);
  const state = resolveFieldState({
    error: f.error,
    isMismatch: noPrefill ? false : !!f.mismatch,
    isPrefilled: noPrefill ? false : f.isPrefilled,
    readOnly,
  });
  return (
    <div className={containerClassName}>
      <label htmlFor={field} className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <div className="relative">
        <input
          id={field}
          type={type}
          // While loading, show a blank value — old data shouldn't linger when
          // a fresh fetch is in flight (would mislead the user about state).
          value={loading ? "" : f.value}
          onChange={f.onChange}
          onBlur={f.onBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete={autoComplete}
          readOnly={readOnly || loading}
          aria-invalid={f.error ? true : undefined}
          aria-busy={loading || undefined}
          className={`${inputClass(state)} ${readOnly ? "cursor-default" : ""}`}
        />
        {loading && (
          <span
            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
            aria-hidden="true"
          >
            <Spinner size="sm" label={null} />
          </span>
        )}
      </div>
      <FieldHint error={f.error} isPrefilled={noPrefill ? false : f.isPrefilled} mismatch={noPrefill ? undefined : f.mismatch} />
    </div>
  );
}
